import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, DatePicker, Progress, Row, Space, Statistic, Table, Tag, Typography, message } from 'antd';
import { Column } from '@ant-design/charts';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';

import AppLayout from '../../../components/layout/AppLayout';
import { useSignupApi, SIGNUP_STEPS, type SignupFunnelStats } from '../../../api/auth/signup';

const { Text } = Typography;
const { RangePicker } = DatePicker;

/** Ordre d'affichage du tunnel — un abandon ne fait pas partie de la progression. */
const FUNNEL_ORDER = [
    SIGNUP_STEPS.LANDING_CTA,
    SIGNUP_STEPS.ACCOUNT,
    SIGNUP_STEPS.PARENT_ROLE,
    SIGNUP_STEPS.CHILDREN,
    SIGNUP_STEPS.SPOUSE,
    SIGNUP_STEPS.PAYMENT,
    SIGNUP_STEPS.CHECKOUT,
    SIGNUP_STEPS.COMPLETED,
    SIGNUP_STEPS.ACTIVATED,
] as const;

type FunnelRow = {
    key: string;
    step: string;
    freemium: number;
    premium: number;
    total: number;
    retention: number;
};

/**
 * Écran admin « tunnel d'inscription ».
 *
 * Croise deux sources : les événements front (où les visiteurs décrochent) et
 * les comptes réellement créés en base (combien s'activent vraiment). C'est ce
 * croisement qui lève le flou de monitoring entre freemium et premium.
 */
const SignupFunnelDashboard: React.FC = () => {
    const { t } = useTranslation();
    const signupApi = useSignupApi();

    const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(29, 'day'), dayjs()]);
    const [stats, setStats] = useState<SignupFunnelStats | null>(null);
    const [loading, setLoading] = useState(false);
    const [forbidden, setForbidden] = useState(false);

    const load = useCallback(
        async (from: Dayjs, to: Dayjs) => {
            setLoading(true);
            try {
                const data = await signupApi.getFunnelStats(
                    from.format('YYYY-MM-DD'),
                    // borne haute exclusive côté serveur : on ajoute un jour pour
                    // inclure la journée sélectionnée.
                    to.add(1, 'day').format('YYYY-MM-DD')
                );
                setStats(data);
                setForbidden(false);
            } catch (err: any) {
                if (err?.response?.status === 403) {
                    setForbidden(true);
                } else {
                    message.error(t('signupFunnel.loadError', 'Impossible de charger les statistiques du tunnel.'));
                }
            } finally {
                setLoading(false);
            }
        },
        [signupApi, t]
    );

    useEffect(() => {
        void load(range[0], range[1]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range]);

    const stepLabels = useMemo<Record<string, string>>(
        () => ({
            [SIGNUP_STEPS.LANDING_CTA]: t('signupFunnel.steps.landingCta', 'Clic sur le CTA'),
            [SIGNUP_STEPS.ACCOUNT]: t('signupFunnel.steps.account', 'Email / Google'),
            [SIGNUP_STEPS.PARENT_ROLE]: t('signupFunnel.steps.parentRole', 'Papa ou maman'),
            [SIGNUP_STEPS.CHILDREN]: t('signupFunnel.steps.children', "Nombre d'enfants"),
            [SIGNUP_STEPS.SPOUSE]: t('signupFunnel.steps.spouse', 'Compte conjoint'),
            [SIGNUP_STEPS.PAYMENT]: t('signupFunnel.steps.payment', 'Paiement affiché'),
            [SIGNUP_STEPS.CHECKOUT]: t('signupFunnel.steps.checkout', 'Code de paiement généré'),
            [SIGNUP_STEPS.COMPLETED]: t('signupFunnel.steps.completed', 'Comptes créés'),
            [SIGNUP_STEPS.ACTIVATED]: t('signupFunnel.steps.activated', 'Compte activé'),
            [SIGNUP_STEPS.ABANDONED]: t('signupFunnel.steps.abandoned', 'Abandon'),
        }),
        [t]
    );

    const funnelRows = useMemo<FunnelRow[]>(() => {
        if (!stats) return [];
        const byStep = new Map<string, { freemium: number; premium: number }>();
        stats.steps.forEach((s) => {
            const entry = byStep.get(s.step) ?? { freemium: 0, premium: 0 };
            if (s.plan === 'freemium') entry.freemium += s.funnels;
            if (s.plan === 'premium') entry.premium += s.funnels;
            byStep.set(s.step, entry);
        });

        const entryTotal = (byStep.get(SIGNUP_STEPS.LANDING_CTA)?.freemium ?? 0) + (byStep.get(SIGNUP_STEPS.LANDING_CTA)?.premium ?? 0);

        return FUNNEL_ORDER.map((step) => {
            const entry = byStep.get(step) ?? { freemium: 0, premium: 0 };
            const total = entry.freemium + entry.premium;
            return {
                key: step,
                step,
                freemium: entry.freemium,
                premium: entry.premium,
                total,
                retention: entryTotal > 0 ? Math.round((total / entryTotal) * 100) : 0,
            };
        });
    }, [stats]);

    const abandonCount = useMemo(
        () => stats?.steps.filter((s) => s.step === SIGNUP_STEPS.ABANDONED).reduce((sum, s) => sum + s.funnels, 0) ?? 0,
        [stats]
    );

    const totals = useMemo(() => {
        const plans = stats?.plans ?? [];
        return plans.reduce(
            (acc, p) => ({
                accounts: acc.accounts + p.accounts,
                active: acc.active + p.active_accounts,
                children: acc.children + p.children_created,
                google: acc.google + p.google_accounts,
            }),
            { accounts: 0, active: 0, children: 0, google: 0 }
        );
    }, [stats]);

    const funnelColumns: ColumnsType<FunnelRow> = [
        {
            title: t('signupFunnel.columns.step', 'Étape'),
            dataIndex: 'step',
            key: 'step',
            render: (step: string) => <Text strong>{stepLabels[step] ?? step}</Text>,
        },
        {
            title: t('signupFunnel.columns.freemium', 'Freemium'),
            dataIndex: 'freemium',
            key: 'freemium',
            align: 'right',
            width: 120,
        },
        {
            title: t('signupFunnel.columns.premium', 'Premium'),
            dataIndex: 'premium',
            key: 'premium',
            align: 'right',
            width: 120,
        },
        {
            title: t('signupFunnel.columns.retention', 'Rétention'),
            dataIndex: 'retention',
            key: 'retention',
            width: 220,
            render: (value: number, row) => (
                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                    <Progress percent={value} size="small" strokeColor="#2D9CC9" />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {t('signupFunnel.funnelsCount', '{{count}} tunnel', { count: row.total })}
                    </Text>
                </Space>
            ),
        },
    ];

    const planColumns: ColumnsType<SignupFunnelStats['plans'][number]> = [
        {
            title: t('signupFunnel.columns.plan', 'Formule'),
            dataIndex: 'plan',
            key: 'plan',
            render: (plan: string) => (
                <Tag color={plan === 'premium' ? 'gold' : 'blue'}>{plan || t('signupFunnel.unknown', 'inconnue')}</Tag>
            ),
        },
        {
            title: t('signupFunnel.columns.accounts', 'Comptes parents'),
            dataIndex: 'accounts',
            key: 'accounts',
            align: 'right',
        },
        {
            title: t('signupFunnel.columns.activated', 'Activés'),
            key: 'active',
            align: 'right',
            render: (_, row) => (
                <Space size={6}>
                    <Text strong>{row.active_accounts}</Text>
                    <Text type="secondary">
                        ({row.accounts > 0 ? Math.round((row.active_accounts / row.accounts) * 100) : 0}%)
                    </Text>
                </Space>
            ),
        },
        {
            title: t('signupFunnel.columns.method', 'Google / Email'),
            key: 'method',
            align: 'right',
            render: (_, row) => `${row.google_accounts} / ${row.email_accounts}`,
        },
        {
            title: t('signupFunnel.columns.children', 'Enfants créés'),
            dataIndex: 'children_created',
            key: 'children_created',
            align: 'right',
        },
    ];

    const chartData = useMemo(
        () =>
            (stats?.daily ?? []).map((d) => ({
                day: d.day,
                plan: d.plan || t('signupFunnel.unknown', 'inconnue'),
                value: d.completed,
            })),
        [stats, t]
    );

    if (forbidden) {
        return (
            <AppLayout title={t('signupFunnel.title', "Tunnel d'inscription")}>
                <Alert
                    type="error"
                    showIcon
                    message={t('signupFunnel.forbidden', 'Accès réservé aux administrateurs.')}
                />
            </AppLayout>
        );
    }

    return (
        <AppLayout title={t('signupFunnel.title', "Tunnel d'inscription")} loading={loading}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Card>
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <Text type="secondary">
                            {t(
                                'signupFunnel.description',
                                'Où les visiteurs décrochent entre le clic sur la landing et l’activation du compte, formule par formule.'
                            )}
                        </Text>
                        <RangePicker
                            value={range}
                            allowClear={false}
                            onChange={(values) => {
                                if (values && values[0] && values[1]) setRange([values[0], values[1]]);
                            }}
                        />
                    </Space>
                </Card>

                <Row gutter={[16, 16]}>
                    <Col xs={12} md={6}>
                        <Card>
                            <Statistic
                                title={t('signupFunnel.stats.accounts', 'Comptes parents créés')}
                                value={totals.accounts}
                                valueStyle={{ color: '#2992bf', fontWeight: 700 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card>
                            <Statistic
                                title={t('signupFunnel.stats.activated', 'Comptes activés')}
                                value={totals.active}
                                suffix={totals.accounts > 0 ? `/ ${totals.accounts}` : undefined}
                                valueStyle={{ color: '#52c41a', fontWeight: 700 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card>
                            <Statistic
                                title={t('signupFunnel.stats.children', 'Comptes enfants créés')}
                                value={totals.children}
                                valueStyle={{ color: '#8bbe1e', fontWeight: 700 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card>
                            <Statistic
                                title={t('signupFunnel.stats.abandons', 'Abandons')}
                                value={abandonCount}
                                valueStyle={{ color: '#fa8c16', fontWeight: 700 }}
                            />
                        </Card>
                    </Col>
                </Row>

                <Card title={t('signupFunnel.funnelTitle', 'Progression étape par étape')}>
                    <Table<FunnelRow>
                        rowKey="key"
                        size="middle"
                        columns={funnelColumns}
                        dataSource={funnelRows}
                        pagination={false}
                    />
                </Card>

                <Card title={t('signupFunnel.planTitle', 'Comptes créés par formule')}>
                    <Table
                        rowKey="plan"
                        size="middle"
                        columns={planColumns}
                        dataSource={stats?.plans ?? []}
                        pagination={false}
                        locale={{ emptyText: t('signupFunnel.noData', 'Aucune inscription sur la période.') }}
                    />
                </Card>

                <Card title={t('signupFunnel.dailyTitle', 'Inscriptions abouties par jour')}>
                    {chartData.length > 0 ? (
                        <Column
                            data={chartData}
                            xField="day"
                            yField="value"
                            colorField="plan"
                            group
                            height={280}
                            axis={{ y: { title: t('signupFunnel.dailyAxis', 'Inscriptions') } }}
                        />
                    ) : (
                        <Text type="secondary">{t('signupFunnel.noData', 'Aucune inscription sur la période.')}</Text>
                    )}
                </Card>
            </Space>
        </AppLayout>
    );
};

export default SignupFunnelDashboard;
